import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/layout/PageHeader';
import CloseButton from '../components/CloseButton';
import SEO from '../components/SEO';
import '../styles/About.css';

const About: React.FC = () => {
  const navigate = useNavigate();
  const [updateStatus, setUpdateStatus] = useState<'idle' | 'checking' | 'available' | 'latest' | 'error'>('idle');
  const [latestVersion, setLatestVersion] = useState<string>('');

  const handleClose = () => {
    navigate(-1); // Go back to previous page
  };

  // Get version from package.json or environment variable
  const getAppVersion = () => {
    // Try to get version from build-time environment variable first
    if (process.env.REACT_APP_VERSION) {
      return process.env.REACT_APP_VERSION;
    }
    
    // Fallback to current package.json version
    return '0.1.27';
  };

  const getBuildDate = () => {
    // This would typically be injected during build
    return process.env.REACT_APP_BUILD_DATE || new Date().toLocaleDateString();
  };

  const checkForUpdates = async () => {
    setUpdateStatus('checking');
    
    try {
      // Check if it's a PWA
      const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                    (window.navigator as any).standalone || 
                    document.referrer.includes('android-app://');
      
      // Show brief checking state
      await new Promise(resolve => setTimeout(resolve, 500));
      
      if (isPWA) {
        // For PWA (especially mobile), force a hard refresh to get updates
        // Mobile PWAs often don't handle service worker updates well
        if (window.confirm('This will refresh the app to check for the latest version. Continue?')) {
          try {
            // Try to update service worker first if available
            if ('serviceWorker' in navigator) {
              const registration = await navigator.serviceWorker.getRegistration();
              if (registration) {
                await registration.update();
                
                // If there's a waiting service worker, activate it
                if (registration.waiting) {
                  registration.waiting.postMessage({ type: 'SKIP_WAITING' });
                }
              }
            }
          } catch (swError) {
            console.warn('Service worker update failed:', swError);
          }
          
          // Force a complete app refresh with cache bypass
          // Clear any app cache if possible
          if ('caches' in window) {
            try {
              const cacheNames = await caches.keys();
              await Promise.all(cacheNames.map(name => caches.delete(name)));
            } catch (cacheError) {
              console.warn('Cache clearing failed:', cacheError);
            }
          }
          
          // Force reload with timestamp to bypass all caching
          const url = new URL(window.location.href);
          url.searchParams.set('_pwa_refresh', Date.now().toString());
          window.location.href = url.toString();
          return;
        } else {
          // User cancelled, show current version
          setUpdateStatus('latest');
          setLatestVersion(getAppVersion());
        }
      } else {
        // For regular web app, do a hard refresh (equivalent to Cmd+Shift+R)
        if (window.confirm('This will refresh the page to check for the latest version. Continue?')) {
          // Force a hard refresh (bypass cache) - equivalent to Cmd+Shift+R
          // Add timestamp to force cache bypass
          const url = new URL(window.location.href);
          url.searchParams.set('_refresh', Date.now().toString());
          window.location.href = url.toString();
          return;
        } else {
          // User cancelled, show current version
          setUpdateStatus('latest');
          setLatestVersion(getAppVersion());
        }
      }
    } catch (error) {
      console.error('Error checking for updates:', error);
      setUpdateStatus('error');
    }
  };

  const getVersionDisplayText = () => {
    switch (updateStatus) {
      case 'checking':
        return 'Checking...';
      case 'available':
        return `${getAppVersion()} (Update available!)`;
      case 'latest':
        return `${getAppVersion()} (Up to date)`;
      case 'error':
        return `${getAppVersion()} (Check failed)`;
      default:
        return `${getAppVersion()} (Tap to refresh)`;
    }
  };

  const getVersionStatusClass = () => {
    switch (updateStatus) {
      case 'checking':
        return 'version-checking';
      case 'available':
        return 'version-update-available';
      case 'latest':
        return 'version-latest';
      case 'error':
        return 'version-error';
      default:
        return 'version-default';
    }
  };

  return (
    <>
      <SEO 
        title="About Banana Cup - Climbing App | Paraclimbing & Climbing Logger"
        description="Learn about Banana Cup climbing app - the ultimate climbing progress tracker and training coach. Developed by Paraclimbing.be VZW for the climbing and paraclimbing community."
        keywords="about banana cup, climbing app info, paraclimbing.be, climbing app information, paraklimmen app, bananacup.be about"
        path="/about"
      />
      <div className="about-container">
      <PageHeader title="About the Banana Cup" showMenu={false} />
      <CloseButton onClose={handleClose} />
      
      <div className="about-content">
        <section className="about-section">
          <h2>What is Paraclimbing?</h2>
          <p>
            Para climbing is climbing for people with physical disabilities. 
            It provides an inclusive environment where climbers of all abilities can challenge 
            themselves and enjoy the sport together.
          </p>
          
          <h3>Classification System</h3>
          <p>
            Paraclimbing uses a classification system to ensure fair competition:
          </p>
          <ul>
            <li><strong>AU (Arm/Upper Limb):</strong> Impairment affecting arm or hand function</li>
            <li><strong>AL (Leg/Lower Limb):</strong> Impairment affecting leg or foot function</li>
            <li><strong>VI (Visual):</strong> Visual impairment</li>
            <li><strong>RP (Restricted Position):</strong> Neurological conditions affecting movement</li>
          </ul>
          
          <h3>Benefits of Paraclimbing</h3>
          <ul>
            <li>Builds physical strength and endurance</li>
            <li>Improves mental focus and problem-solving skills</li>
            <li>Provides social interaction and community</li>
            <li>Boosts confidence and self-esteem</li>
            <li>Offers adaptive techniques for different abilities</li>
          </ul>
        </section>

        <section className="about-section">
          <h2>About the Banana Cup</h2>
          <p>
            The Banana Cup is a climbing competition designed to promote inclusivity and 
            accessibility in the sport. It provides a platform for climbers of all abilities 
            to showcase their skills and compete in a supportive environment.
          </p>
          
          <h3>Features</h3>
          <ul>
            <li>Track climbing routes and progress</li>
            <li>Log different climbing styles (boulder, rope)</li>
            <li>Join gym communities</li>
            <li>Participate in ranking competitions</li>
            <li>Share feedback on routes</li>
            <li>Monitor personal statistics</li>
          </ul>
        </section>

        <section className="about-section version-info">
          <h2>App Information</h2>
          <div className="version-details">
            <div className="version-item version-clickable" onClick={checkForUpdates}>
              <span className="version-label">Version:</span>
              <span className={`version-value ${getVersionStatusClass()}`}>
                {getVersionDisplayText()}
              </span>
            </div>
            <div className="version-item">
              <span className="version-label">Build Date:</span>
              <span className="version-value">{getBuildDate()}</span>
            </div>
            <div className="version-item">
              <span className="version-label">Environment:</span>
              <span className="version-value">{process.env.NODE_ENV || 'development'}</span>
            </div>
          </div>
        </section>

        <section className="about-section">
          <h2>Get Involved</h2>
          <p>
            Interested in paraclimbing? Contact your local climbing gym to learn about 
            adaptive climbing programs and equipment. Many gyms offer specialized instruction 
            and adaptive gear to help climbers of all abilities get started.
          </p>
        </section>
      </div>
    </div>
    </>
  );
};

export default About;
